type LoadingOptions = {
  text?: string;
  // If provided, focus will be returned here after hide()
  returnFocusTo?: HTMLElement | null;
};

let __loadingReturnFocus: HTMLElement | null = null;

function ensureLoadingModal(): HTMLElement {
  let modal = document.getElementById("loadingModal") as HTMLElement | null;
  if (!modal) {
    // Lazy create if not present in HTML
    modal = document.createElement("div");
    modal.id = "loadingModal";
    modal.className = "loading-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("aria-label", "Loading");
    modal.innerHTML = `
      <div class="loading-modal__backdrop"></div>
      <div class="loading-modal__dialog" role="document" tabindex="-1">
        <div class="loading-modal__spinner" aria-hidden="true"></div>
        <div class="loading-modal__text" id="loadingModalText">Working...</div>
      </div>
    `;
    document.body.appendChild(modal);
  }
  return modal;
}

export function showLoading(opts: LoadingOptions = {}) {
  const modal = ensureLoadingModal();
  const dialog = modal.querySelector<HTMLElement>(".loading-modal__dialog")!;
  const textEl = modal.querySelector<HTMLElement>("#loadingModalText");

  if (opts.text && textEl) textEl.textContent = opts.text;

  // Accessibility: mark visible & focus the dialog (focus trap lite)
  modal.setAttribute("data-open", "true");
  modal.setAttribute("aria-hidden", "false");

  // Remember origin focus to restore later
  __loadingReturnFocus = (opts.returnFocusTo ?? (document.activeElement as HTMLElement)) || null;

  // Tiny async to ensure element is attached before focusing
  setTimeout(() => dialog.focus(), 0);

  // Prevent page scroll while modal open (optional)
  document.documentElement.style.overflow = "hidden";

  // Basic escape key handling
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") e.preventDefault(); // modal is not dismissible here
    // trap focus inside dialog
    if (e.key === "Tab") {
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusables).filter(el => !el.hasAttribute("disabled"));
      const first = list[0] || dialog;
      const last = list[list.length - 1] || dialog;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  };

  dialog.addEventListener("keydown", onKey);
  (modal as any).__onKey = onKey; // keep a ref for cleanup
}

export function hideLoading() {
  const modal = document.getElementById("loadingModal");
  if (!modal) return;

  modal.removeAttribute("data-open");
  modal.setAttribute("aria-hidden", "true");

  // Restore scroll
  document.documentElement.style.overflow = "";

  // Cleanup key handler
  const dialog = modal.querySelector<HTMLElement>(".loading-modal__dialog");
  const onKey = (modal as any).__onKey as ((e: KeyboardEvent) => void) | undefined;
  if (dialog && onKey) dialog.removeEventListener("keydown", onKey);
  delete (modal as any).__onKey;

  // Restore focus
  if (__loadingReturnFocus) {
    __loadingReturnFocus.focus();
    __loadingReturnFocus = null;
  }
}

/**
 * Convenience helper to wrap any Promise with the modal.
 * Usage: await withLoading(apiCall(), "Loading clients...");
 */
export async function withLoading<T>(promise: Promise<T>, text = "Working..."): Promise<T> {
  showLoading({ text });
  try {
    return await promise;
  } finally {
    hideLoading();
  }
}